import { PlusOutlined } from "@ant-design/icons"
import {
  App,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  TimePicker,
} from "antd"
import { type CheckboxChangeEvent } from "antd/es/checkbox"
import dayjs from "dayjs"
import { cloneElement, useCallback, useEffect, useMemo, useState } from "react"

import { curriculumAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import CheckboxWrapper from "#blocks/Form/CheckboxWrapper"
import DisabledFieldTooltip from "#blocks/Form/DisabledFieldTooltip"
import { SFormHeader } from "#blocks/Form/formComponents"
import { Curriculum } from "#classes/Curriculum"
import { NULL_ID } from "#classes/Firebase"
import { ROOM_OPTIONS } from "#constants/classOptions"
import { type AddClassFormValues } from "#customTypes/composedTypes/classTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { type Uuid } from "#customTypes/globalTypes"
import SemesterDropdown from "#forms/SemesterDropdown"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import { CheckUsageQuery, useCheckUsage } from "#hooks/reactQuery/useCheckUsage"
import { disabledHours, STANDARD_TIME_FORMAT } from "#utils/dateHelpers"
import { filterFormOptions } from "#utils/formHelpers"

import AddAssistantToClassForm from "./AddAssistantToClass/AddAssistantToClassForm"
import AddClassAssistantList from "./AddClassAssistantList"
import AddClassStudentList from "./AddClassStudentList"
import AddClassTeacherList from "./AddClassTeacherList"
import AddStudentToClassForm from "./AddStudentToClass/AddStudentToClassForm"
import AddTeacherToClassForm from "./AddTeacherToClass/AddTeacherToClassForm"
import ClassSessionOverview from "./ClassSessionOverview"

const ROW_GUTTER = 16

const SClassMemberHeader = ({
  onAdd,
  isEdit,
  title,
}: {
  onAdd: () => void
  isEdit: boolean
  title: string
}) => {
  return (
    <Flex gap={16} style={{ alignItems: "center", marginBottom: 10 }}>
      <Flex style={{ fontWeight: "bold" }}>{title}</Flex>
      <Button
        onClick={onAdd}
        size="small"
        shape="circle"
        icon={<PlusOutlined />}
        style={{ marginBottom: 1 }}
        disabled={!isEdit}
      />
    </Flex>
  )
}

const AddClassForm = ({
  initialValues,
}: {
  initialValues: AddClassFormValues | undefined
}) => {
  const isEdit = initialValues?.id != null
  const form = Form.useFormInstance<AddClassFormValues>()
  const { message } = App.useApp()
  const [isZoomClass, setIsZoomClass] = useState(
    initialValues?.zoomLink != null
  )
  const semester = Form.useWatch("semester", form) ?? initialValues?.semester

  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false)
  const [isAddTeacherModalOpen, setIsAddTeacherModalOpen] = useState(false)
  const [isAddAssistantModalOpen, setIsAddAssistantModalOpen] = useState(false)

  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null
  )
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(
    null
  )
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    null
  )

  const classId = initialValues?.id || NULL_ID
  const queries = useMemo(
    () =>
      [
        {
          docType: DOC_TYPES.STUDENTS,
          queries: {
            [`attendance.${classId}`]: {
              value: null,
              comparator: COMPARATORS.DOES_NOT_EQUAL,
            },
          },
        },
        {
          docType: DOC_TYPES.STUDENTS,
          queries: {
            [`assistantAttendance.${classId}`]: {
              value: null,
              comparator: COMPARATORS.DOES_NOT_EQUAL,
            },
          },
        },
        {
          docType: DOC_TYPES.USERS,
          queries: {
            [`teacherAttendance.${classId}`]: {
              value: null,
              comparator: COMPARATORS.DOES_NOT_EQUAL,
            },
          },
        },
        {
          docType: DOC_TYPES.MAKEUPS,
          queries: {
            missedClassId: {
              value: classId,
              comparator: COMPARATORS.EQUALS,
            },
          },
        },
        {
          docType: DOC_TYPES.MAKEUPS,
          queries: {
            makeupClassId: {
              value: classId,
              comparator: COMPARATORS.EQUALS,
            },
          },
        },
        {
          docType: DOC_TYPES.TRYOUTS,
          queries: {
            classId: {
              value: classId,
              comparator: COMPARATORS.EQUALS,
            },
          },
        },
      ] as CheckUsageQuery[],
    [classId]
  )

  const { data: isUsed } = useCheckUsage(
    DOC_TYPES.CLASSES,
    initialValues?.id,
    queries
  )

  const onSelectStudent = (studentId: Uuid) => {
    setSelectedStudentId(studentId)
    setIsAddStudentModalOpen(true)
  }

  const onSelectTeacher = (teacherId: Uuid) => {
    setSelectedTeacherId(teacherId)
    setIsAddTeacherModalOpen(true)
  }

  const onSelectAssistant = (assistantId: Uuid) => {
    setSelectedAssistantId(assistantId)
    setIsAddAssistantModalOpen(true)
  }

  const queryConfig: FirebaseQueryConfig = useMemo(() => {
    return {
      queries: {
        semester: {
          value: semester,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }
  }, [semester])

  // Get curriculum options
  const semesterCurriculums = useFilteredDocs<CurriculumConstructor>(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    queryConfig
  )

  const curriculumOptions = useMemo(() => {
    return semesterCurriculums
      ?.map((curriculum) => {
        const curriculumObj = new Curriculum(curriculum)
        return {
          label: curriculumObj.getDisplayName(),
          value: curriculum.id,
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [semesterCurriculums])

  const curriculumId =
    Form.useWatch("curriculumId", form) ?? initialValues?.curriculumId

  const curriculum = semesterCurriculums?.find((c) => c.id === curriculumId)
  const { group } = curriculum ?? {}

  const handleCurriculumChange = useCallback(
    (selectedCurriculumId: Uuid) => {
      const selectedCurriculum = semesterCurriculums?.find(
        (c) => c.id === selectedCurriculumId
      )

      if (
        selectedCurriculum &&
        new Curriculum(selectedCurriculum).hasNoProjects()
      ) {
        void message.warning(
          "This curriculum has no projects. Add projects before saving a class with it."
        )
      }
    },
    [message, semesterCurriculums]
  )

  useEffect(() => {
    if (group) {
      form.setFieldValue("group", group)
    }
  }, [group, form])

  // Changing the default teacher in AddTeacherToClassForm doesn't auto update form values
  useEffect(() => {
    if (initialValues?.defaultTeacherId) {
      form.setFieldValue("defaultTeacherId", initialValues.defaultTeacherId)
    }
  }, [initialValues?.defaultTeacherId, form])

  const handleZoomClassChange = (e: CheckboxChangeEvent) => {
    setIsZoomClass(e.target.checked)
  }

  return (
    <>
      <Row gutter={48}>
        {/* LEFT SIDE */}
        <Col span={14}>
          <SFormHeader>Curriculum</SFormHeader>

          <Row gutter={ROW_GUTTER}>
            <Col span={6}>
              <SemesterDropdown
                disabledTooltip={
                  isUsed
                    ? "Cannot change semester - this class is currently being used."
                    : undefined
                }
              />
            </Col>

            <Col span={10}>
              <DisabledFieldTooltip
                disabled={isUsed}
                tooltip={
                  isUsed
                    ? "Cannot change curriculum - this class is currently being used."
                    : undefined
                }
              >
                <Form.Item
                  name="curriculumId"
                  label="Curriculum Plan"
                  rules={[
                    {
                      required: true,
                      message: "Please select a curriculum!",
                    },
                  ]}
                >
                  <Select
                    disabled={isUsed}
                    showSearch
                    placeholder="Select curriculum"
                    options={curriculumOptions}
                    filterOption={filterFormOptions}
                    onChange={handleCurriculumChange}
                  />
                </Form.Item>
              </DisabledFieldTooltip>
            </Col>

            <Col span={3}>
              <Form.Item label="Cycle">{curriculum?.cycle}</Form.Item>
            </Col>

            <Col span={5}>
              <Form.Item label="# Sessions">
                {curriculum?.numberOfSessions}
              </Form.Item>
            </Col>
          </Row>

          <SFormHeader>Class Info</SFormHeader>
          <Row gutter={ROW_GUTTER}>
            <Col span={13}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: "Please input the class name!",
                  },
                ]}
              >
                <Input placeholder="Enter class name" />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="room"
                label="Room"
                rules={[{ required: true, message: "Please select a room!" }]}
              >
                <Select
                  showSearch
                  placeholder="Select a room"
                  filterOption={filterFormOptions}
                  options={ROOM_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="capacity"
                label="Capacity"
                rules={[
                  { required: true, message: "Please enter a capacity!" },
                ]}
              >
                <Input placeholder="Capacity" />
              </Form.Item>
            </Col>
          </Row>

          <SFormHeader>Scheduling</SFormHeader>
          {/* DATE AND TIME PICKERS */}
          <Row gutter={ROW_GUTTER}>
            <Col span={8}>
              <DisabledFieldTooltip
                disabled={isUsed}
                tooltip={
                  isUsed
                    ? "Cannot change start date - this class is currently being used."
                    : undefined
                }
              >
                <Form.Item
                  name="startDate"
                  label="Start Date"
                  rules={[
                    { required: true, message: "Please select a start date!" },
                  ]}
                >
                  <DatePicker
                    disabled={isUsed}
                    format="M/D/YY - dddd"
                    style={{ width: "100%" }}
                    minDate={dayjs("2025-01-01")}
                  />
                </Form.Item>
              </DisabledFieldTooltip>
            </Col>

            <Col span={9}>
              <DisabledFieldTooltip
                disabled={isUsed}
                tooltip={
                  isUsed
                    ? "Cannot change start and end time - this class is currently being used."
                    : undefined
                }
              >
                <Form.Item
                  name="times"
                  label="Start and End Time"
                  rules={[{ required: true, message: "Please select times!" }]}
                >
                  <TimePicker.RangePicker
                    format={STANDARD_TIME_FORMAT}
                    disabled={isUsed}
                    use12Hours={false}
                    minuteStep={5}
                    hideDisabledOptions
                    needConfirm={false}
                    cellRender={(current, info) => {
                      // Time picker labels are numbers not dates
                      const typesafeCurrent = Number(current)
                      // Reformat 24h time into 12h time so we don't need to show AM/PM
                      if (info.subType === "hour") {
                        const hour12 = ((typesafeCurrent + 11) % 12) + 1
                        return cloneElement(info.originNode, {}, `${hour12}`)
                      }
                      return info.originNode
                    }}
                    disabledTime={() => ({
                      disabledHours: () => disabledHours,
                    })}
                  />
                </Form.Item>
              </DisabledFieldTooltip>
            </Col>

            <Col span={6}>
              <Form.Item label="Waitlist">
                <Input placeholder="Enter waitlist" />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* RIGHT SIDE */}
        <Col span={10}>
          <SFormHeader>Options</SFormHeader>
          {/* CHECKBOXES */}
          <Row gutter={ROW_GUTTER} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <CheckboxWrapper
                name="isZoomClass"
                label="Zoom Class"
                onChange={handleZoomClassChange}
              />

              {isZoomClass && (
                <Form.Item
                  name="zoomLink"
                  style={{ marginLeft: 24, marginBottom: 16 }}
                >
                  <Input placeholder="Enter zoom link" />
                </Form.Item>
              )}
              <Row>
                <CheckboxWrapper
                  name="isSummerCamp"
                  label="Summer Camp"
                  disabledTooltip={
                    isUsed
                      ? "Cannot change summer camp status - this class is currently being used."
                      : undefined
                  }
                />
              </Row>
              <Row>
                <CheckboxWrapper
                  name="isMakeupAllowed"
                  label="Makeups Allowed"
                />
              </Row>
              <Row>
                <CheckboxWrapper
                  name="isTryoutAllowed"
                  label="Tryouts Allowed"
                />
              </Row>
            </Col>
          </Row>

          {/* NOTES */}
          <Row gutter={ROW_GUTTER}>
            <Col span={24}>
              <Form.Item
                name="notes"
                label="Notes"
                rules={[{ required: false }]}
              >
                <Input.TextArea placeholder="Enter any notes" rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={ROW_GUTTER} style={{ padding: "10px 0 20px" }}>
        <Col span={12}>
          <SClassMemberHeader
            onAdd={() => setIsAddStudentModalOpen(true)}
            isEdit={isEdit}
            title={`Students (${initialValues?.studentIds?.length ?? 0})`}
          />
          {isEdit ? (
            <AddClassStudentList
              onStudentClick={onSelectStudent}
              studentIds={initialValues?.studentIds ?? []}
            />
          ) : (
            <Flex style={{ marginTop: 6 }}>
              Save the class before adding students.
            </Flex>
          )}
        </Col>

        <Col span={6}>
          <SClassMemberHeader
            onAdd={() => setIsAddTeacherModalOpen(true)}
            isEdit={isEdit}
            title={`Teachers (${initialValues?.teacherIds?.length ?? 0})`}
          />
          {!isEdit && (
            <Flex style={{ marginTop: 6 }}>
              Save the class before adding teachers.
            </Flex>
          )}

          {isEdit && (
            <AddClassTeacherList
              onTeacherClick={onSelectTeacher}
              teacherIds={initialValues?.teacherIds ?? []}
            />
          )}
        </Col>

        <Col span={6}>
          <SClassMemberHeader
            onAdd={() => setIsAddAssistantModalOpen(true)}
            isEdit={isEdit}
            title={`TAs (${initialValues?.assistantIds?.length ?? 0})`}
          />
          {!isEdit && (
            <Flex style={{ marginTop: 6 }}>
              Save the class before adding assistants.
            </Flex>
          )}

          {isEdit && (
            <AddClassAssistantList
              onAssistantClick={onSelectAssistant}
              assistantIds={initialValues?.assistantIds ?? []}
            />
          )}
        </Col>
      </Row>

      <Row gutter={ROW_GUTTER}>
        <Col span={24}>
          <SFormHeader>Sessions</SFormHeader>
          <ClassSessionOverview
            curriculum={curriculum}
            onSelectStudent={onSelectStudent}
            onSelectTeacher={onSelectTeacher}
            onSelectAssistant={onSelectAssistant}
          />
        </Col>
      </Row>

      {/* Dummy field to allow us to interact with initialValues */}
      <Form.Item name="sessionIds" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="studentIds" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="assistantIds" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="teacherIds" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="id" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="startTime" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="endTime" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="group" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="defaultTeacherId" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>

      {isAddStudentModalOpen &&
        isEdit &&
        initialValues != null &&
        initialValues.id != null &&
        initialValues.name != null &&
        curriculum != null && (
          <AddStudentToClassForm
            sessionIds={initialValues.sessionIds ?? []}
            studentId={selectedStudentId}
            classId={initialValues.id}
            className={initialValues.name}
            curriculumId={initialValues.curriculumId}
            onClose={() => {
              setIsAddStudentModalOpen(false)
              setSelectedStudentId(null)
            }}
            open={isAddStudentModalOpen}
          />
        )}

      {isAddTeacherModalOpen && isEdit && curriculum != null && (
        <AddTeacherToClassForm
          sessionIds={initialValues?.sessionIds ?? []}
          teacherId={selectedTeacherId}
          onClose={() => {
            setIsAddTeacherModalOpen(false)
            setSelectedTeacherId(null)
          }}
          open={isAddTeacherModalOpen}
        />
      )}

      {isAddAssistantModalOpen && isEdit && curriculum != null && (
        <AddAssistantToClassForm
          sessionIds={initialValues?.sessionIds ?? []}
          assistantId={selectedAssistantId}
          onClose={() => {
            setIsAddAssistantModalOpen(false)
            setSelectedAssistantId(null)
          }}
          open={isAddAssistantModalOpen}
        />
      )}
    </>
  )
}

export default AddClassForm
